import React, { useState } from "react";
import { Button, Input, Select } from "antd";
import { PlusOutlined, DeleteOutlined } from "@ant-design/icons";
import { useQv4Theme } from "../../helpers";

// Shared part shape used by both the Create Query form (CustomerEditDrawer)
// and the Parts-tab "Add part" form (WorkflowTaskDrawer) — extracted from
// CustomerEditDrawer's original inline JSX so both places get the same
// searchable-with-add-new part name field and the same side/condition
// dropdowns instead of the Parts tab's older flat free-text fields.
// condition_preference: 1 matches the original Create Query "Add Part"
// button's default (inherited as-is, not re-derived — presumably ID 1 is
// whatever the conditionOptions list treats as its default/first entry).
export const EMPTY_PART = { part_name: "", quantity: 1, side: null, condition_preference: 1, part_number: "" };

// `onAddNewPartInfo(value, idx)` is left to the caller (rather than owned
// here) because it both hits the network (POST /whatsapp/add_new_part_info)
// and updates a partsInfoOptions cache that's shared across a whole drawer
// session, not scoped to one PartsEditor instance.
export function PartsEditor({
  parts, setParts,
  sideOptions = [], conditionOptions = [],
  partsInfoOptions = [], partsInfoLoading = false,
  onAddNewPartInfo,
  label = "Parts",
}) {
  const { t } = useQv4Theme();
  const [activePartSearch, setActivePartSearch] = useState("");

  const updatePart = (idx, fields) => setParts(prev => {
    const next = [...prev];
    next[idx] = { ...next[idx], ...fields };
    return next;
  });

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
        <div style={{ fontSize: 12, color: "#8c8c8c" }}>{label}</div>
        <Button
          size="small"
          icon={<PlusOutlined />}
          onClick={() => setParts(prev => [...prev, { ...EMPTY_PART }])}
        >
          Add Part
        </Button>
      </div>
      {parts.length === 0 ? (
        <div style={{ fontSize: 12, color: "#bfbfbf", textAlign: "center", padding: "6px 0" }}>
          No parts added yet
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {parts.map((part, idx) => (
            <div key={idx} style={{
              border: "1px solid #d0e4ff", borderRadius: 6,
              padding: "8px 10px", background: t.cardBg,
              display: "flex", flexDirection: "column", gap: 6,
            }}>
              {/* Row 1: name + qty + delete */}
              <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                <Select
                  showSearch
                  size="small"
                  style={{ flex: 1 }}
                  aria-label="Part name"
                  placeholder="Part name *"
                  loading={partsInfoLoading}
                  value={part.part_name || undefined}
                  onSearch={setActivePartSearch}
                  onChange={(val, opt) => {
                    setActivePartSearch("");
                    updatePart(idx, { part_name: String(opt?.label ?? val ?? "") });
                  }}
                  filterOption={(input, opt) =>
                    String(opt.label ?? opt.value ?? "").toLowerCase().includes(input.toLowerCase())
                  }
                  options={partsInfoOptions}
                  popupRender={menu => (
                    <>
                      {menu}
                      {activePartSearch &&
                        !partsInfoOptions.some(o =>
                          String(o.label ?? o.value ?? "").toLowerCase() === activePartSearch.toLowerCase()
                        ) && (
                        <div
                          style={{ padding: "6px 12px", cursor: "pointer", color: "#1677ff", fontSize: 12, borderTop: `1px solid ${t.border}` }}
                          onMouseDown={e => {
                            e.preventDefault();
                            onAddNewPartInfo?.(activePartSearch, idx);
                            setActivePartSearch("");
                          }}
                        >
                          + Add "{activePartSearch}"
                        </div>
                      )}
                    </>
                  )}
                />
                <Input
                  type="number"
                  min={1}
                  value={part.quantity}
                  onChange={e => updatePart(idx, { quantity: Math.max(1, parseInt(e.target.value) || 1) })}
                  style={{ width: 56 }}
                  size="small"
                  prefix={<span style={{ fontSize: 10, color: "#8c8c8c" }}>×</span>}
                />
                <Button
                  type="text" danger size="small"
                  icon={<DeleteOutlined />}
                  onClick={() => setParts(prev => prev.filter((_, i) => i !== idx))}
                />
              </div>
              {/* Row 2: side + condition + part number */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 6 }}>
                <Select
                  size="small"
                  placeholder="Side"
                  allowClear
                  value={part.side ?? null}
                  onChange={val => updatePart(idx, { side: val ?? null })}
                  options={sideOptions}
                />
                <Select
                  size="small"
                  placeholder="Condition"
                  allowClear
                  value={part.condition_preference ?? null}
                  onChange={val => updatePart(idx, { condition_preference: val ?? null })}
                  options={conditionOptions}
                />
                <Input
                  size="small"
                  placeholder="Part #"
                  value={part.part_number}
                  onChange={e => updatePart(idx, { part_number: e.target.value })}
                />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
